import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import { Button, Dialog, DialogProps, SxProps, TextField, Theme, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useFormik } from 'formik';
import React, { memo } from 'react';

import { Autocomplete, AutocompleteOption } from '~/components/Autocomplete';
import { Tooltip } from '~/components/Tooltip';
import { EXPIRE_DATE_FORMAT } from '~/constants/dateFormats';
import { useNotification } from '~/hooks/useNotification';
import { useGenerateLinkMutation } from '~/services/rtkQuery/admin';
import { GenerateLinkFormValues } from '~/types/formValues';
import { generateLinkFormSchema } from '~/utils/formSchemas';

import styles from './styles.module.scss';

const EMPTY_OPTIONS: AutocompleteOption[] = [];

interface Props extends Omit<DialogProps, 'onClose'> {
  companyId: number;
  onClose: () => void;
}

const FIELD_SX: SxProps<Theme> = { mb: '24px' };

const Component: React.FC<Props> = ({ open, companyId, onClose, ...props }) => {
  const [generateLinkMutation, { isLoading: isLoadingGenerateLinkMutation }] =
    useGenerateLinkMutation();
  const { handleApiError, showNotification } = useNotification();
  const formik = useFormik<GenerateLinkFormValues>({
    initialValues: {
      email: '',
      ccEmails: [],
    },
    validationSchema: generateLinkFormSchema,
    onSubmit: async values => {
      const ccEmails = values.ccEmails.map(email => email.value as string);

      try {
        const response = await generateLinkMutation({
          company_id: companyId,
          email: values.email,
          emails: ccEmails,
        }).unwrap();

        showNotification({
          type: 'success',
          title: response.title,
          message: response.message,
          description: `Available until ${format(new Date(response.result.expired_at), EXPIRE_DATE_FORMAT)}`,
        });

        onClose();
      } catch (e) {
        handleApiError(e);
      }
    },
  });

  return (
    <Dialog open={open} onClose={onClose} {...props}>
      <form className={styles.modal} onSubmit={formik.handleSubmit}>
        <button aria-label="close" className={styles.modal__close} type="button" onClick={onClose}>
          <CloseIcon sx={{ width: '18px', height: '18px', color: 'var(--gray-2-color)' }} />
        </button>
        <Typography color="text.primary" mb="32px" variant="h5">
          Please enter a valid email to generate a link
        </Typography>
        <TextField
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email ? formik.errors.email : undefined}
          label={
            <div className={styles['modal__label-tooltip']}>
              <Tooltip title="Primary email for link delivery and notifications">
                <InfoIcon
                  sx={{
                    width: '17px',
                    height: '17px',
                    color: 'var(--gray-6-color)',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
              Email*
            </div>
          }
          name="email"
          sx={FIELD_SX}
          type="email"
          value={formik.values.email}
          variant="outlined"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
        <Autocomplete
          creatable
          creatableWithoutCheckbox
          creatableWithoutOptionLabelPrefix
          multiple
          error={formik.touched.ccEmails && !!formik.errors.ccEmails}
          helperText={formik.touched.ccEmails ? (formik.errors.ccEmails as string) : undefined}
          options={EMPTY_OPTIONS}
          placeholder="CC Emails"
          sx={FIELD_SX}
          value={formik.values.ccEmails}
          onBlur={() => formik.setFieldTouched('ccEmails')}
          onChange={value => formik.setFieldValue('ccEmails', value)}
        />
        <Button loading={isLoadingGenerateLinkMutation} type="submit">
          Generate & Send Link
        </Button>
      </form>
    </Dialog>
  );
};

export const GenerateLinkModal = memo(Component);
